/**
 * Agent Update Progress Component - v4.0
 * 
 * Componente inline para mostrar progresso de OTA Update em tempo real.
 * Recebe updates via Socket.IO do backend.
 * 
 * Estilo UniFi: compacto, inline, informativo.
 */

import React, { useState, useEffect } from 'react';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  RefreshCw, 
  Check, 
  X, 
  AlertTriangle,
  Download,
  Package,
  Shield,
  HardDrive,
  Cog,
  RotateCcw,
} from 'lucide-react';
import { io } from 'socket.io-client';

const API = process.env.REACT_APP_BACKEND_URL;

// Icons for each step
const STEP_ICONS = {
  1: Download,      // Comando recebido
  2: Download,      // Baixando
  3: Shield,        // Verificando
  4: HardDrive,     // Backup
  5: Package,       // Instalando
  6: Cog,           // Dependências
  7: RefreshCw,     // Reiniciando
  8: Check,         // Completo
};

/**
 * Componente de progresso inline para OTA Update.
 * 
 * Props:
 * - deviceId: ID do equipamento
 * - onRetry: Callback para tentar novamente
 * - compact: Se true, usa layout mais compacto
 */
export const AgentUpdateProgress = ({ deviceId, onRetry, compact = false, fallbackToVersion = null, onProgressEvent = null }) => {
  const [progress, setProgress] = useState(null);
  const [socket, setSocket] = useState(null);

  useEffect(() => {
    // Conectar ao Socket.IO
    const newSocket = io(API, {
      path: '/api/socket.io',
      transports: ['websocket', 'polling'],
    });

    newSocket.on('connect', () => {
      console.log('AgentUpdateProgress: Socket connected');
    });

    newSocket.on('agent_update_progress', (data) => {
      console.log('AgentUpdateProgress: Received update', data);
      if (data.device_id === deviceId) {
        setProgress(data);
        if (onProgressEvent) {
          onProgressEvent(data);
        }
      }
    });

    newSocket.on('disconnect', () => {
      console.log('AgentUpdateProgress: Socket disconnected');
    });

    setSocket(newSocket);

    return () => {
      newSocket.disconnect();
    };
  }, [deviceId, onProgressEvent]);

  // Mostrar estado inicial "Aguardando..." enquanto não há progresso
  if (!progress) {
    if (compact) {
      return (
        <div className="flex items-center gap-2 text-sm" data-testid="update-waiting-compact">
          <RefreshCw className="w-4 h-4 animate-spin text-blue-400" />
          <span className="text-muted-foreground">Aguardando agent...</span>
        </div>
      );
    }
    
    return (
      <div 
        className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 space-y-2"
        data-testid="update-waiting-full"
      >
        <div className="flex items-center gap-2">
          <RefreshCw className="w-4 h-4 text-blue-400 animate-spin" />
          <span className="text-sm font-medium text-blue-300">
            Comando enviado!
          </span>
        </div>
        <p className="text-xs text-muted-foreground">
          Aguardando o agent iniciar o processo de atualização...
        </p>
      </div>
    );
  }

  const { 
    percent, 
    status, 
    current_action, 
    from_version, 
    to_version,
    error_detail,
    step,
    elapsed_seconds,
  } = progress;

  // Renderizar baseado no status
  if (status === 'success') {
    return (
      <SuccessState 
        fromVersion={from_version}
        toVersion={to_version}
        fallbackToVersion={fallbackToVersion}
        elapsedSeconds={elapsed_seconds}
        compact={compact}
        onDismiss={() => setProgress(null)}
      />
    );
  }

  if (status === 'error' || status === 'rolled_back') {
    return (
      <ErrorState 
        status={status}
        errorDetail={error_detail}
        fromVersion={from_version}
        compact={compact}
        onRetry={onRetry}
        onDismiss={() => setProgress(null)}
      />
    );
  }

  // In progress
  return (
    <InProgressState 
      percent={percent}
      currentAction={current_action}
      step={step}
      fromVersion={from_version}
      toVersion={to_version}
      compact={compact}
    />
  );
};

/**
 * Estado: Em progresso
 */
const InProgressState = ({ 
  percent, 
  currentAction, 
  step, 
  fromVersion, 
  toVersion,
  compact 
}) => {
  const StepIcon = STEP_ICONS[step] || RefreshCw;

  if (compact) {
    return (
      <div className="flex items-center gap-2 text-sm" data-testid="update-progress-compact">
        <RefreshCw className="w-4 h-4 animate-spin text-blue-400" />
        <Progress value={percent} className="w-24 h-2" />
        <span className="text-muted-foreground">{percent}%</span>
      </div>
    );
  }

  return (
    <div 
      className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 space-y-2"
      data-testid="update-progress-full"
    >
      <div className="flex items-center gap-2">
        <StepIcon className="w-4 h-4 text-blue-400 animate-pulse" />
        <span className="text-sm font-medium text-blue-300">
          Atualizando...
        </span>
        {fromVersion && toVersion && (
          <Badge variant="outline" className="text-xs ml-auto">
            v{fromVersion} → v{toVersion}
          </Badge>
        )}
      </div>
      
      <Progress value={percent} className="h-2" />
      
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>{currentAction}</span>
        <span>{percent}%</span>
      </div>
    </div>
  );
};

/**
 * Estado: Sucesso
 */
const SuccessState = ({ 
  fromVersion, 
  toVersion, 
  fallbackToVersion,
  elapsedSeconds,
  compact,
  onDismiss 
}) => {
  const resolvedToVersion = (toVersion && toVersion !== 'unknown') ? toVersion : (fallbackToVersion || toVersion || 'desconhecida');

  if (compact) {
    return (
      <div className="flex items-center gap-2 text-sm text-green-400" data-testid="update-success-compact">
        <Check className="w-4 h-4" />
        <span>Atualizado para v{resolvedToVersion}</span>
      </div>
    );
  }

  return (
    <div 
      className="bg-green-500/10 border border-green-500/30 rounded-lg p-3"
      data-testid="update-success-full"
    >
      <div className="flex items-center gap-2">
        <Check className="w-5 h-5 text-green-400" />
        <span className="text-sm font-medium text-green-300">
          Atualizado para v{resolvedToVersion}
        </span>
        {elapsedSeconds && (
          <span className="text-xs text-muted-foreground ml-auto">
            Concluído em {elapsedSeconds}s
          </span>
        )}
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={onDismiss}
          className="h-6 w-6 p-0 ml-2"
        >
          <X className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );
};

/**
 * Estado: Erro ou Rollback
 */
const ErrorState = ({ 
  status, 
  errorDetail, 
  fromVersion,
  compact,
  onRetry,
  onDismiss 
}) => {
  const isRolledBack = status === 'rolled_back';
  
  if (compact) {
    return (
      <div className="flex items-center gap-2 text-sm text-red-400" data-testid="update-error-compact">
        {isRolledBack ? (
          <RotateCcw className="w-4 h-4" />
        ) : (
          <X className="w-4 h-4" />
        )}
        <span>Falha na atualização</span>
        {onRetry && (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onRetry}
            className="h-6 text-xs"
          >
            Tentar novamente
          </Button>
        )}
      </div>
    );
  }

  return (
    <div 
      className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 space-y-2"
      data-testid="update-error-full"
    >
      <div className="flex items-center gap-2">
        {isRolledBack ? (
          <>
            <AlertTriangle className="w-5 h-5 text-yellow-400" />
            <span className="text-sm font-medium text-yellow-300">
              Falha na atualização, versão anterior restaurada
            </span>
          </>
        ) : (
          <>
            <X className="w-5 h-5 text-red-400" />
            <span className="text-sm font-medium text-red-300">
              Falha na atualização
            </span>
          </>
        )}
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={onDismiss}
          className="h-6 w-6 p-0 ml-auto"
        >
          <X className="w-3 h-3" />
        </Button>
      </div>
      
      {errorDetail && (
        <p className="text-xs text-muted-foreground">
          Erro: {errorDetail}
        </p>
      )}
      
      {isRolledBack && fromVersion && (
        <p className="text-xs text-muted-foreground">
          Versão restaurada: v{fromVersion}
        </p>
      )}
      
      {onRetry && (
        <Button 
          variant="outline" 
          size="sm" 
          onClick={onRetry}
          className="w-full mt-2"
        >
          <RefreshCw className="w-3 h-3 mr-2" />
          Tentar novamente
        </Button>
      )}
    </div>
  );
};

export default AgentUpdateProgress;
